/**
 * WebSocket Service - управление WebSocket соединением
 */
import type {
  WebSocketConfig,
  WebSocketMessage,
  WebSocketEventType,
  WebSocketStatus,
} from '../../types/websocket.types';
import { getImpersonationTicket } from '../../features/impersonation/impersonationStorage';

export class WebSocketService {
  private ws: WebSocket | null = null;
  private config: {
    url: string;
    token?: string;
    getToken?: () => string | null | Promise<string | null>;
    reconnectInterval: number;
    maxReconnectAttempts: number;
    heartbeatInterval: number;
    autoConnect: boolean;
  };
  private status: WebSocketStatus = 'DISCONNECTED' as WebSocketStatus;
  private reconnectAttempts = 0;
  private reconnectTimeout: NodeJS.Timeout | null = null;
  private heartbeatInterval: NodeJS.Timeout | null = null;
  private authTimeout: NodeJS.Timeout | null = null;
  private eventHandlers: Map<WebSocketEventType, Set<(payload: unknown, message: WebSocketMessage<unknown>) => void>> = new Map();
  private messageQueue: WebSocketMessage<unknown>[] = [];
  private statusChangeCallback: ((status: WebSocketStatus) => void) | null = null;
  
  private readonly MAX_QUEUE_SIZE = 100;

  constructor(config: WebSocketConfig) {
    this.config = {
      url: config.url,
      token: config.token,
      getToken: config.getToken,
      reconnectInterval: config.reconnectInterval ?? 3000,
      maxReconnectAttempts: config.maxReconnectAttempts ?? 5,
      heartbeatInterval: config.heartbeatInterval ?? 60000,
      autoConnect: config.autoConnect ?? true,
    };

    if (this.config.autoConnect) {
      this.connect();
    }
  }

  /**
   * Получает актуальный токен: через callback (свежий) или из конфига (статичный)
   */
  private async resolveToken(): Promise<string | null> {
    if (this.config.getToken) {
      try {
        return await this.config.getToken();
      } catch {
        return null;
      }
    }
    return this.config.token ?? null;
  }

  /**
   * Подключение к WebSocket серверу
   */
  connect(): void {
    if (this.ws?.readyState === WebSocket.OPEN || this.ws?.readyState === WebSocket.CONNECTING) {
      if (import.meta.env.DEV) {
        console.log('⚠️ WebSocket: Уже подключен или подключается');
      }
      return;
    }

    this.setStatus('CONNECTING' as WebSocketStatus);
    if (import.meta.env.DEV) {
      console.log('🔌 WebSocket: Подключение к', this.config.url);
    }

    try {
      this.ws = new WebSocket(this.config.url);
      this.setupEventListeners();
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('❌ WebSocket: Ошибка подключения', error);
      }
      this.setStatus('ERROR' as WebSocketStatus);
      this.handleReconnect();
    }
  }

  /**
   * Отключение от WebSocket сервера
   * 🔧 FIX: Предотвращает утечки памяти через очистку всех ресурсов
   */
  disconnect(): void {
    if (import.meta.env.DEV) {
      console.log('🔌 WebSocket: Отключение...');
    }
    
    // Очищаем таймауты и интервалы
    this.clearReconnectTimeout();
    this.clearHeartbeatInterval();
    
    // Удаляем event listeners перед закрытием соединения
    if (this.ws) {
      this.removeWebSocketListeners();
      this.ws.close(1000, 'Client disconnecting');
      this.ws = null;
    }
    
    // Очищаем очередь сообщений
    this.messageQueue = [];
    
    // Очищаем обработчики событий
    this.eventHandlers.clear();
    
    this.setStatus('DISCONNECTED' as WebSocketStatus);
  }

  /**
   * Отправка сообщения
   * 🔧 FIX: Ограничиваем размер очереди для предотвращения утечек памяти
   */
  send<T = unknown>(message: WebSocketMessage<T>): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      try {
        this.ws.send(JSON.stringify(message));
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error('❌ WebSocket: Ошибка отправки', error);
        }
      }
    } else {
      // Проверяем размер очереди
      if (this.messageQueue.length >= this.MAX_QUEUE_SIZE) {
        if (import.meta.env.DEV) {
          console.warn('⚠️ WebSocket: Очередь сообщений заполнена, удаляем старое');
        }
        this.messageQueue.shift(); // Удаляем самое старое сообщение
      }
      
      if (import.meta.env.DEV) {
        console.warn('⚠️ WebSocket: Не подключен, добавляем в очередь', message.type);
      }
      this.messageQueue.push(message as WebSocketMessage<unknown>);
    }
  }

  /**
   * Подписка на тип события (множественные обработчики)
   */
  on(eventType: WebSocketEventType, handler: (payload: unknown, message: WebSocketMessage<unknown>) => void): () => void {
    if (!this.eventHandlers.has(eventType)) {
      this.eventHandlers.set(eventType, new Set());
    }
    this.eventHandlers.get(eventType)!.add(handler);

    return () => {
      this.eventHandlers.get(eventType)?.delete(handler);
    };
  }

  /**
   * Отписка от типа события
   */
  off(eventType: WebSocketEventType, handler?: (payload: unknown, message: WebSocketMessage<unknown>) => void): void {
    if (handler) {
      this.eventHandlers.get(eventType)?.delete(handler);
    } else {
      this.eventHandlers.delete(eventType);
    }
  }

  /**
   * Подписка на изменение статуса
   */
  onStatusChange(callback: (status: WebSocketStatus) => void): void {
    this.statusChangeCallback = callback;
  }

  /**
   * Получение текущего статуса
   */
  getStatus(): WebSocketStatus {
    return this.status;
  }

  /**
   * Проверка подключения
   */
  isConnected(): boolean {
    return this.status === ('CONNECTED' as WebSocketStatus) && this.ws?.readyState === WebSocket.OPEN;
  }

  /**
   * Настройка обработчиков событий WebSocket
   * 🔧 FIX: Очищаем старые listeners перед установкой новых
   */
  private setupEventListeners(): void {
    if (!this.ws) return;

    // Очищаем старые обработчики (если были)
    this.removeWebSocketListeners();

    this.ws.onopen = () => {
      if (import.meta.env.DEV) {
        console.log('✅ WebSocket: Подключено, отправляем AUTH...');
      }
      this.setStatus('CONNECTING' as WebSocketStatus);
      
      // 🔧 FIX #3: Таймаут на получение CONNECTED (10 секунд)
      this.authTimeout = setTimeout(() => {
        if (this.status === ('CONNECTING' as WebSocketStatus)) {
          if (import.meta.env.DEV) {
            console.error('❌ WebSocket: Таймаут AUTH (10 сек) - CONNECTED не получен');
          }
          this.setStatus('ERROR' as WebSocketStatus);
          this.handleReconnect();
        }
      }, 10000);
      
      void this.sendAuth(this.ws);
    };

    this.ws.onmessage = (event: MessageEvent) => {
      try {
        const data = event.data;
        if (typeof data !== 'string') {
          if (import.meta.env.DEV) {
            console.error('❌ WebSocket: Неверный тип данных сообщения');
          }
          return;
        }
        
        const message = JSON.parse(data) as WebSocketMessage<unknown>;
        
        if (!message.type || !message.timestamp) {
          if (import.meta.env.DEV) {
            console.error('❌ WebSocket: Неверный формат сообщения', message);
          }
          return;
        }
        
        this.handleMessage(message);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        if (import.meta.env.DEV) {
          console.error('❌ WebSocket: Ошибка парсинга сообщения', errorMessage);
        }
      }
    };

    this.ws.onerror = (error: Event) => {
      if (import.meta.env.DEV) {
        console.error('❌ WebSocket: Ошибка соединения', error);
      }
      this.setStatus('ERROR' as WebSocketStatus);
    };

    this.ws.onclose = (event: CloseEvent) => {
      if (import.meta.env.DEV) {
        console.log('🔌 WebSocket: Соединение закрыто', event.code, event.reason);
      }
      this.setStatus('DISCONNECTED' as WebSocketStatus);
      this.clearHeartbeatInterval();
      
      // Автоматическое переподключение, если не было явного закрытия
      if (event.code !== 1000) {
        this.handleReconnect();
      }
    };
  }

  /**
   * Отправка AUTH с токеном, полученным на момент (пере)подключения.
   * Токен запрашивается заново при каждом подключении: закэшированный при старте
   * JWT живёт ~1 час, и переподключение с ним бэкенд отклоняет (jwt expired).
   */
  private async sendAuth(socket: WebSocket | null): Promise<void> {
    const currentToken = await this.resolveToken();

    // Пока ждали токен, сокет могли закрыть или заменить новым —
    // его судьбой уже управляют onclose/новое подключение
    if (!socket || socket !== this.ws || socket.readyState !== WebSocket.OPEN) {
      return;
    }

    if (!currentToken) {
      if (import.meta.env.DEV) {
        console.error('❌ WebSocket: Нет токена для AUTH');
      }
      this.clearAuthTimeout();
      this.setStatus('ERROR' as WebSocketStatus);
      // Закрываем не-1000 кодом → onclose запустит переподключение с новой попыткой получить токен
      socket.close(4001, 'No token');
      return;
    }

    const impersonationTicket = getImpersonationTicket();
    const authMessage = {
      type: 'AUTH' as WebSocketEventType,
      payload: impersonationTicket
        ? { token: currentToken, impersonationTicket }
        : { token: currentToken },
      timestamp: new Date().toISOString(),
    };

    try {
      socket.send(JSON.stringify(authMessage));
      if (import.meta.env.DEV) {
        console.log('📤 WebSocket: AUTH сообщение отправлено');
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('❌ WebSocket: Ошибка отправки AUTH', error);
      }
      this.setStatus('ERROR' as WebSocketStatus);
      this.clearAuthTimeout();
    }
  }

  /**
   * Обработка входящих сообщений
   */
  private handleMessage(message: WebSocketMessage<unknown>): void {
    if (import.meta.env.DEV) {
      console.log('📨 WebSocket: Получено сообщение', message.type, message.payload);
    }
    
    // Специальная обработка CONNECTED события после успешной аутентификации
    if (message.type === 'CONNECTED' as WebSocketEventType) {
      if (import.meta.env.DEV) {
        console.log('✅ WebSocket: Аутентификация успешна, готов к работе', message.payload);
      }
      
      // 🔧 FIX #4: Сбрасываем reconnectAttempts только после успешной AUTH
      this.reconnectAttempts = 0;
      
      // 🔧 FIX #3: Очищаем таймаут AUTH
      this.clearAuthTimeout();
      
      this.setStatus('CONNECTED' as WebSocketStatus);
      this.startHeartbeat();
      this.flushMessageQueue();
    }
    
    // Отказ в подписке на отдельный канал: соединение живо, статус не трогаем
    if (message.type === 'SUBSCRIPTION_DENIED' as WebSocketEventType) {
      if (import.meta.env.DEV) {
        console.warn('⚠️ WebSocket: Подписка на канал отклонена', message.payload);
      }
    }

    // Обработка ERROR события
    if (message.type === 'ERROR' as WebSocketEventType) {
      if (import.meta.env.DEV) {
        console.error('❌ WebSocket: Получена ошибка от сервера', message.payload);
      }
      this.setStatus('ERROR' as WebSocketStatus);
    }
    
    // 🔧 FIX #5: Вызываем все зарегистрированные обработчики
    const handlers = this.eventHandlers.get(message.type);
    if (handlers && handlers.size > 0) {
      handlers.forEach(handler => {
        try {
          handler(message.payload, message);
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error';
          if (import.meta.env.DEV) {
            console.error('❌ WebSocket: Ошибка в обработчике', errorMessage);
          }
        }
      });
    }
  }

  /**
   * Обработка переподключения
   * 🔧 FIX: Exponential backoff с jitter
   */
  private handleReconnect(): void {
    if (this.reconnectAttempts >= this.config.maxReconnectAttempts) {
      if (import.meta.env.DEV) {
        console.error('❌ WebSocket: Достигнут лимит попыток переподключения');
      }
      this.setStatus('ERROR' as WebSocketStatus);
      return;
    }

    this.reconnectAttempts++;
    const baseDelay = this.config.reconnectInterval * Math.pow(2, this.reconnectAttempts - 1);
    const jitter = Math.random() * 1000; // 0-1000ms случайная задержка
    const delay = Math.min(baseDelay + jitter, 30000); // Максимум 30 секунд
    
    if (import.meta.env.DEV) {
      console.log(`🔄 WebSocket: Переподключение через ${Math.round(delay)}ms (попытка ${this.reconnectAttempts}/${this.config.maxReconnectAttempts})`);
    }
    this.setStatus('RECONNECTING' as WebSocketStatus);

    this.reconnectTimeout = setTimeout(() => {
      this.connect();
    }, delay);
  }

  /**
   * Запуск heartbeat
   */
  private startHeartbeat(): void {
    this.clearHeartbeatInterval();
    
    this.heartbeatInterval = setInterval(() => {
      if (this.isConnected()) {
        this.send({
          type: 'HEARTBEAT' as WebSocketEventType,
          payload: { timestamp: new Date().toISOString() },
          timestamp: new Date().toISOString(),
        });
      }
    }, this.config.heartbeatInterval);
  }

  /**
   * Отправка накопленных сообщений
   * 🔧 FIX #1: Предотвращаем бесконечную рекурсию
   */
  private flushMessageQueue(): void {
    if (this.messageQueue.length > 0 && this.ws?.readyState === WebSocket.OPEN) {
      if (import.meta.env.DEV) {
        console.log(`📤 WebSocket: Отправка ${this.messageQueue.length} сообщений из очереди`);
      }
      
      // Копируем очередь и сразу очищаем оригинал
      const queue = [...this.messageQueue];
      this.messageQueue = [];
      
      // Отправляем напрямую, не через send()
      queue.forEach(message => {
        try {
          this.ws!.send(JSON.stringify(message));
        } catch (error) {
          if (import.meta.env.DEV) {
            console.error('❌ WebSocket: Ошибка отправки из очереди', error);
          }
        }
      });
    }
  }

  /**
   * Установка статуса
   * 🔧 FIX #6: Оповещаем подписчиков об изменении
   */
  private setStatus(status: WebSocketStatus): void {
    if (this.status !== status) {
      this.status = status;
      this.statusChangeCallback?.(status);
    }
  }

  /**
   * Удаление WebSocket event listeners
   * 🔧 FIX: Предотвращает утечки памяти при переподключении
   */
  private removeWebSocketListeners(): void {
    if (this.ws) {
      this.ws.onopen = null;
      this.ws.onmessage = null;
      this.ws.onerror = null;
      this.ws.onclose = null;
    }
  }

  /**
   * Очистка таймаута переподключения
   */
  private clearReconnectTimeout(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
  }

  /**
   * Очистка интервала heartbeat
   */
  private clearHeartbeatInterval(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  /**
   * Очистка таймаута аутентификации
   */
  private clearAuthTimeout(): void {
    if (this.authTimeout) {
      clearTimeout(this.authTimeout);
      this.authTimeout = null;
    }
  }
}


